<template>
  <div class="flex">
    <div
      class="fixed top-30 z-40 w-88 flex-shrink-0 border-r border-slate-400 bg-pure-white"
    >
      <div class="py-6 px-6 border-b border-slate-400 flex flex-col gap-3">
        <div>
          <div class="mb-1"><BlTextBody2 text="Appointments" /></div>
          <BlTextTitle3 text="Rooms" />
        </div>
        <div
          class="py-1 px-1 flex bg-slate-400"
          :style="{ borderRadius: '8px', height: '2.375rem' }"
        >
          <div
            v-for="opt in previewTypeOptions"
            :key="opt.value"
            class="h-full w-full flex items-center justify-center gap-1 rounded cursor-pointer transition-all duration-300"
            :class="[
              previewTypeInput === opt.value ? 'bg-pure-white' : 'bg-slate-400'
            ]"
            @click="onChangePreviewType(opt.value)"
          >
            <BlIcon :icon="opt.icon" color="slate-6" class="w-4 h-4" />
            <BlTextSubtitle3 :text="opt.label" />
          </div>
        </div>
      </div>

      <div
        v-if="pageLoading"
        class="w-full grid place-items-center"
        :style="{ height: 'calc(100vh - 18.5rem)' }"
      ></div>
      <div
        v-else
        class="overflow-y-auto"
        :style="{ height: 'calc(100vh - 18.5rem)' }"
      >
        <div v-if="enableBranch">
          <RoomGroupBranch
            v-for="branch in branches"
            :key="branch.id"
            :title="branch.name"
            :rooms="rooms.filter(i => i.branch_id === branch.id)"
            :selectedRoomId="selectedRoomId"
            @select-room="onSelectRoom($event)"
          />
          <RoomGroupBranch
            title="No branch"
            :rooms="rooms.filter(i => !i.branch_id)"
            :selectedRoomId="selectedRoomId"
            @select-room="onSelectRoom($event)"
          />
        </div>
        <div v-else>
          <RoomGroupBranch
            title="All rooms"
            :rooms="rooms"
            :selectedRoomId="selectedRoomId"
            @select-room="onSelectRoom($event)"
          />
        </div>
      </div>
    </div>

    <div v-if="previewTypeInput === 'calendar'" class="pl-88">
      <div
        class="h-12 px-6 sticky top-30 z-30 bg-slate-300 border-b border-slate-400 flex items-center justify-between gap-4"
        :style="{ minWidth: 'calc(100vw - 22rem - 0.5rem)' }"
      >
        <div class="sticky top-30 left-94">
          <BlTextBody2 :text="masterDate" />
        </div>
        <div class="sticky top-30 right-6 flex items-center gap-2">
          <div>
            <BlSelectBox
              v-model="timeslotView"
              :options="timeslotViewOptions"
              dense
              @change="$emit('set-timeslot-view', $event)"
            />
          </div>
          <BlInputButton
            type="outlined"
            dense
            label="Jump to"
            @clicked="jumpToOpened = true"
          />
          <BlInputButton
            type="outlined"
            dense
            label="Today"
            @clicked="onDayClick({ date: new Date() })"
          />
          <BlInputButton
            type="outlined"
            dense
            class="rounded-full"
            icon="chevron-left"
            @clicked="onNextClick(-1)"
          />
          <BlInputButton
            type="outlined"
            dense
            class="rounded-full"
            icon="chevron-right"
            @clicked="onNextClick(1)"
          />
        </div>
      </div>
      <Timeslots
        :isCompact="timeslotView === 'compact'"
        :selectedTherapist="null"
        :selectedRoom="rooms.find(i => i.id === selectedRoomId)"
        :selectedDate="date"
        :headers="timeslotHeaders"
        :loading="previewLoading"
        @after-commit="fetchTimeslots"
      />
    </div>

    <div v-if="previewTypeInput === 'list'" class="pl-88 bg-slate-100">
      <div
        class="h-16 px-6 sticky top-30 z-20 bg-slate-300 border-b border-slate-400 flex items-center justify-between gap-4"
        :style="{ minWidth: 'calc(100vw - 22rem - 0.5rem)' }"
      >
        <div class="sticky top-30 left-94">
          <div class="flex items-center gap-4">
            <BlInputSearch
              class="w-80"
              :input="searchInput"
              @changed="onInputSearch"
            />
            <div class="w-40">
              <BlInputFilter :input="filterInput" @changed="onChangeFilter" />
            </div>
          </div>
        </div>
        <div class="sticky top-30 right-6 flex items-center gap-2">
          <BlInputButton
            type="outlined"
            label="Export to CSV"
            :loading="exportLoading"
            @clicked="onExport"
          />
          <BlInputButton
            v-if="currentUser.role.includes('admin')"
            icon="plus"
            label="Appointment"
            @clicked="newAppointmentOpened = true"
          />
        </div>
      </div>

      <div>
        <div v-if="previewLoading" class="w-full h-92 grid place-items-center">
          <div class="blum-loader"></div>
        </div>
        <div
          v-else-if="!appointments.length"
          class="grid place-items-center h-92"
        >
          <BlTextBody2
            v-if="searchInput.model"
            text="Oops, the appointment not found."
            class="lg:w-128 text-slate-700 text-center"
          />
          <BlTextBody2
            v-else
            text="Oops, there are no Appointment available."
            class="lg:w-128 text-slate-700 text-center"
          />
        </div>
        <AppointmentsTable v-else @after-commit="fetchAppointments" />
      </div>

      <div v-if="totalAppointments > 25" class="flex">
        <BlInputPagination
          class="sticky left-94 my-10"
          :style="{ minWidth: 'calc(100vw - 22rem - 3rem - 0.5rem)' }"
          :page="page"
          :perPage="perPage"
          :total="totalAppointments"
          @changed="changePage"
          @changed-per-page="changePerPage"
        />
      </div>
    </div>

    <JumpToDateModal
      v-if="jumpToOpened"
      :currentDate="date"
      dimension="weekly"
      @close-modal="jumpToOpened = false"
      @submit="onDayClick({ date: $event })"
    />
    <NewAppointmentsModal
      v-if="newAppointmentOpened && currentUser.role.includes('admin')"
      :therapist="{ id: null }"
      :room="rooms.find(i => i.id === selectedRoomId) || { id: null }"
      :startDate="''"
      :startTime="''"
      @close-modal="newAppointmentOpened = false"
      @after-commit="fetchAppointments"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import debounce from "lodash/debounce";

import RoomGroupBranch from "../availabilities/room/GroupBranch.vue";
import JumpToDateModal from "../availabilities/timeslot/JumpToDateModal.vue";
import Timeslots from "./timeslot/Timeslots.vue";
import NewAppointmentsModal from "./modal/NewAppointmentsModal.vue";
import AppointmentsTable from "./table/Table.vue";

import BlTextTitle3 from "../BlTextTitle3.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlInputSearch from "../BlInputSearch.vue";
import BlInputFilter from "../BlInputFilter.vue";
import BlInputButton from "../BlInputButton.vue";
import BlSelectBox from "../BlSelectBox.vue";
import BlIcon from "../BlIcon.vue";
import BlInputPagination from "../BlInputPagination.vue";

export default {
  name: "AppointmentsRoom",
  components: {
    RoomGroupBranch,
    JumpToDateModal,
    Timeslots,
    NewAppointmentsModal,
    AppointmentsTable,
    BlTextTitle3,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputSearch,
    BlInputFilter,
    BlInputButton,
    BlSelectBox,
    BlIcon,
    BlInputPagination
  },
  props: {
    timeslotView: { type: String },
    timeslotViewOptions: { type: Array }
    // @set-timeslot-view
  },
  data() {
    const defaultFilter = [{ start_date: "", end_date: "" }, [], [], []];
    return {
      pageLoading: true,
      previewLoading: false,
      exportLoading: false,
      date: new Date(),
      previewTypeInput: "calendar", // calendar | list
      previewTypeOptions: [
        { value: "calendar", label: "Calendar", icon: "calendar-blank" },
        { value: "list", label: "List", icon: "list" }
      ],
      selectedRoomId: 0,
      jumpToOpened: false,
      //
      newAppointmentOpened: false,
      page: 1,
      perPage: 25,
      searchInput: { placeholder: "Search client", model: "" },
      defaultFilter,
      filterInput: {
        model: defaultFilter,
        tabs: [
          { name: "Date", kind: "date" },
          {
            name: "Status",
            kind: "multi-tag",
            options: [
              {
                name: "Scheduled",
                value: "scheduled",
                color: "#FB6514",
                backgroundColor: "#FFF6ED"
              },
              {
                name: "In progress",
                value: "in_progress",
                color: "#487ADF",
                backgroundColor: "#DFEAFA"
              },
              {
                name: "Completed",
                value: "completed",
                color: "#789719",
                backgroundColor: "#F2F8CF"
              },
              {
                name: "Cancelled",
                value: "cancelled",
                color: "#F63D68",
                backgroundColor: "#FFF1F3"
              }
            ]
          },
          {
            name: "Client",
            kind: "multi",
            options: [],
            url: `/api/v1/clients?sort=alphabetical&status=active&query=`,
            fetchPage: 1,
            dataKey: "clients",
            countKey: "total_clients",
            optionKeys: { value: "id", label: "name" }
          },
          {
            name: "Therapist",
            kind: "multi",
            options: [],
            url: `/api/v1/users?sort=name_asc&status=active&query=`,
            fetchPage: 1,
            dataKey: "users",
            countKey: "total_users",
            optionKeys: { value: "id", label: "name" }
          }
        ]
      }
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "branches",
      "rooms",
      "totalRooms",
      "appointments",
      "totalAppointments"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    getCalendarParams() {
      const d = moment(this.date)
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      let params = `?date=${d}&type=weekly`;
      if (this.selectedRoomId) {
        params += `&room_id=${this.selectedRoomId}`;
      }
      return params;
    },
    getListParams() {
      let params = ``;
      if (this.selectedRoomId) {
        params += `&room_id=${this.selectedRoomId}`;
      }
      if (this.searchInput.model) {
        params += `&query=${this.searchInput.model}`;
      }
      //
      const dates = this.filterInput.model.at(0) || {
        start_date: "",
        end_date: ""
      };
      const statuses = this.filterInput.model.at(1) || [];
      const clients = this.filterInput.model.at(2) || [];
      const therapists = this.filterInput.model.at(3) || [];

      if (dates.start_date) {
        params += `&start_date=${dates.start_date}`;
      }
      if (dates.end_date) {
        params += `&end_date=${dates.end_date}`;
      }
      if (statuses.length > 0) {
        params += `&status=${statuses.map(a => a.value).join(",")}`;
      }
      if (clients.length > 0) {
        params += `&client_id=${clients.map(a => a.value).join(",")}`;
      }
      if (therapists.length > 0) {
        params += `&user_id=${therapists.map(a => a.value).join(",")}`;
      }
      return params;
    },
    masterDate() {
      const start = moment(this.date)
        .startOf("isoWeek")
        .format("ddd, DD MMM YYYY");
      const end = moment(this.date)
        .endOf("isoWeek")
        .format("ddd, DD MMM YYYY");
      return `${start} — ${end}`;
    },
    timeslotHeaders() {
      const startDate = moment(this.date)
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      const endDate = moment(this.date)
        .endOf("isoWeek")
        .format("YYYY-MM-DD");

      let current = moment(startDate);

      const dates = [];

      while (current.isSameOrBefore(endDate)) {
        const isSame =
          current.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
        dates.push({
          highlight: isSame,
          value: current.format("YYYY-MM-DD"),
          label: current.format("ddd DD")
        });
        current.add(1, "days");
      }

      return dates;
    }
  },
  async mounted() {
    const isValidDate = d => {
      return d instanceof Date && !isNaN(d);
    };

    const searchParams = this.$route.query;
    if (searchParams["preview_type"]) {
      const found = this.previewTypeOptions.find(
        i => i.value === searchParams["preview_type"]
      );
      if (found) this.previewTypeInput = found.value;
    }
    if (searchParams["date"]) {
      const d = new Date(`${searchParams["date"]}T00:00:00`);
      if (isValidDate(d)) this.date = d;
    }
    if (searchParams["room_id"]) {
      const n = Number(searchParams["room_id"]);
      if (Number(n)) this.selectedRoomId = Number(n);
    }
    //
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["query"]) {
      this.searchInput.model = searchParams["query"];
    }

    this.pageLoading = true;
    this.previewLoading = true;

    const filter = [{ start_date: "", end_date: "" }, [], [], []];
    if (searchParams["start_date"]) {
      filter[0].start_date = searchParams["start_date"];
    }
    if (searchParams["end_date"]) {
      filter[0].end_date = searchParams["end_date"];
    }
    if (searchParams["status"]) {
      const statuses = searchParams["status"].split(",");
      const arr = statuses.map(i => ({ value: i }));
      filter[1] = arr;
    }
    if (searchParams["client_id"]) {
      const ids = searchParams["client_id"].split(",");
      const arr = ids.map(i => ({ value: Number(i) }));
      filter[2] = arr;
    }
    if (searchParams["user_id"]) {
      const ids = searchParams["user_id"].split(",");
      const arr = ids.map(i => ({ value: Number(i) }));
      filter[3] = arr;
    }
    this.filterInput.model = filter;

    const { success, data } = await this.getRooms();
    if (success && !this.selectedRoomId) {
      let id = data.rooms[0].id;
      if (id) this.onSelectRoom(id);
    } else {
      this.onSelectRoom(this.selectedRoomId);
    }
  },
  methods: {
    ...mapActions([
      "getRooms",
      "getTimeslots",
      "getAppointments",
      "exportAppointments"
    ]),
    fetchTimeslots() {
      if (this.$route.path !== "/appointments/room") return;

      this.previewLoading = true;
      this.getTimeslots({ params: this.getCalendarParams }).then(async () => {
        this.previewLoading = false;
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = `/appointments/room${this.getCalendarParams}&preview_type=calendar`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    fetchAppointments() {
      if (this.$route.path !== "/appointments/room") return;

      this.previewLoading = true;
      const params = `?page=${this.page}&per_page=${this.perPage}${this.getListParams}`;
      this.getAppointments({ params }).then(async () => {
        this.previewLoading = false;
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = `/appointments/room${params}&preview_type=list`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    onChangePreviewType(type) {
      this.previewTypeInput = type;
      this.previewLoading = true;
      if (type === "calendar") {
        this.fetchTimeslots();
      }
      if (type === "list") {
        this.fetchAppointments();
      }
    },
    onDayClick(day) {
      const d = day.date;
      this.date = d;
      this.fetchTimeslots();
    },
    onNextClick(num) {
      const change = 7;
      const d = new Date(this.date);
      if (num === 1) {
        this.onDayClick({ date: d.setDate(d.getDate() + change) });
      } else {
        this.onDayClick({ date: d.setDate(d.getDate() - change) });
      }
    },
    onSelectRoom(id) {
      this.selectedRoomId = Number(id);
      if (this.previewTypeInput === "calendar") this.fetchTimeslots();
      if (this.previewTypeInput === "list") return this.fetchAppointments();
    },
    //
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchAppointments();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchAppointments();
    },
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchAppointments();
    }, 1000),
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.fetchAppointments();
    },
    onExport() {
      this.exportLoading = true;
      const params = `?show_by=room${this.getListParams}`;
      this.exportAppointments({ params })
        .then(response => {
          this.exportLoading = false;
          if (response) {
            const href = URL.createObjectURL(response.data);
            const link = document.createElement("a");

            let filename = "";
            let disposition = response.headers["content-disposition"];
            let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            let matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, "");
            }

            link.href = href;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(href);
          }
        })
        .catch(error => {
          this.exportLoading = false;
          console.error(error);
        });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/Room.vue